import { Commit } from 'vuex'
import { Transcript, TranscriptItem } from '../../transcription.api'
import {
  excludeEndingPunctuation,
  mapSpeakers,
  mapUITranscriptSpeakers,
  transcriptToApi,
  transcriptToUI
} from '../../utils'
import { UITranscript } from '../../types/transcript'
import { UISpeaker, Word } from '../../models/word'
import uniq from 'lodash/uniq'

const s = {
  0: ['word1', 'word2', 'word3'],
  10: ['word4', 'word5']
}

export interface TranscriptHistory {
  transcript: UITranscript
  activePartIndex: number
  focusedWordId: string
  cursorPosition: number
}

function mapWordsByTenSeconds(words: Word[]) {
  if (words.length === 0) return {}

  const lastWordEndSecond = words[words.length - 1].end / 1000
  const wordsMap: any = {}

  const groupsCount = Math.ceil(lastWordEndSecond / 10)

  for (let group = 1; group <= groupsCount; group++) {
    const decade = group * 10
    const decadeInMilliseconds = decade * 1000
    const prevDecadeInMilliseconds = decadeInMilliseconds - 10000

    wordsMap[decade] = words.filter(
      (word: Word) =>
        word.start >= prevDecadeInMilliseconds &&
        word.end <= decadeInMilliseconds
    )
  }

  return wordsMap
}
interface SearchWordToHashMap {
  [key: string]: boolean
}

interface ConversationState {
  isSelectTopicActive: boolean
  isSearchOpen: boolean
  searchQuery: string
  originalTranscript?: Transcript
  transcript?: UITranscript
  wordsByTenSeconds?: Word[][]
  editedTranscript?: UITranscript
  searchResults: Word[]
  activeSearchResultIndex: number
  suggestedSpeakersList: UISpeaker[]
  isSpeakersListVisible: boolean
  isSelectionPopupShown: boolean
  selectionBoundaries: DOMRect | null
  isAudioPlaying: boolean
  selectedSpeakerPartTimecode?: number
  continueAudioAfterAction: boolean
  isEditable: boolean
  shouldSave: boolean
  transcriptReady: boolean
  playerTime: number
  isPlaying: boolean
  spelledDecade: number
  spelledSlice: Word[]
  spelledWords: Word[]
  lastWordDisplayed: boolean
  editorLoading: boolean
  browser: string
  shouldUndo: boolean
  spelledPartId: string | null
  transcriptHistory: TranscriptHistory[]
  searchWordToHashMap: SearchWordToHashMap
}

export default {
  namespaced: true,
  state: {
    isSelectTopicActive: false,
    isSearchOpen: false,
    searchQuery: '',
    originalTranscript: null,
    transcript: null,
    editedTranscript: null,
    searchResults: [],
    activeSearchResultIndex: 0,
    suggestedSpeakersList: [],
    isSpeakersListVisible: false,
    selectedSpeakerPartTimecode: null,
    isSelectionPopupShown: false,
    selectionBoundaries: null,
    isAudioPlaying: false,
    continueAudioAfterAction: false,
    isEditable: false,
    shouldSave: false,
    playerTime: 0,
    spelledPartId: null,
    isPlaying: false,
    spelledSlice: [],
    spelledDecade: 0,
    spelledWords: [],
    lastWordDisplayed: false,
    editorLoading: false,
    browser: '',
    shouldUndo: false,
    transcriptReady: false,
    transcriptHistory: [],
    searchWordToHashMap: {}
  },
  getters: {
    isSelectTopicActive: (state: ConversationState): boolean => {
      return state.isSelectTopicActive
    },
    isSearchOpen: (state: ConversationState): boolean => {
      return state.isSearchOpen
    },
    isSafari: (state: ConversationState): boolean => {
      return state.browser === 'Safari'
    },
    searchQuery: (state: ConversationState): string => {
      return state.searchQuery
    },
    searchResults: (state: ConversationState): Word[] => {
      return state.searchResults
    },
    activeSearchResultIndex: (state: ConversationState): number => {
      return state.activeSearchResultIndex
    },
    currentSearchResult: (state: ConversationState): Word | null => {
      return state.activeSearchResultIndex <= state.searchResults.length - 1
        ? state.searchResults[state.activeSearchResultIndex]
        : null
    },
    suggestedSpeakersList: (state: ConversationState): UISpeaker[] => {
      return state.suggestedSpeakersList
    },
    editorLoading: (state: ConversationState): boolean => {
      return state.editorLoading
    },
    isSpeakersListVisible(state: ConversationState): boolean {
      return state.isSpeakersListVisible
    },
    lastWordDisplayed(state: ConversationState): boolean {
      return state.lastWordDisplayed
    },
    suggestedSpeakers(state: ConversationState): UISpeaker[] {
      return state.suggestedSpeakersList
    },
    selectedSpeakerPartTimecode(state: ConversationState): number | undefined {
      return state.selectedSpeakerPartTimecode
    },
    transcript(state: ConversationState): UITranscript | undefined {
      return state.transcript
    },
    originalTranscript(state: ConversationState): Transcript | null {
      return state.originalTranscript
    },
    spelledWords(state: ConversationState): Word[] {
      return state.spelledWords
    },
    spelledPartId(state: ConversationState): string | null {
      return state.spelledPartId
    },
    editedTranscript(state: ConversationState): UITranscript | null {
      return state.editedTranscript
    },
    isSelectionPopupShown(state: ConversationState): boolean {
      return state.isSelectionPopupShown
    },
    selectionBoundaries(state: ConversationState): DOMRect | null {
      return state.selectionBoundaries
    },
    isAudioPlaying(state: ConversationState): boolean {
      return state.isAudioPlaying
    },
    continueAudioAfterAction(state: ConversationState): boolean {
      return state.continueAudioAfterAction
    },
    isEditable(state: ConversationState): boolean {
      return state.isEditable
    },
    shouldSave(state: ConversationState): boolean {
      return state.shouldSave
    },
    shouldUndo(state: ConversationState): boolean {
      return state.shouldUndo
    },
    transcriptWordsToApi(state: ConversationState): TranscriptItem[] {
      return state.transcript ? transcriptToApi(state.transcript).words : []
    },
    transcriptHistory(state: ConversationState): TranscriptHistory[] {
      return state.transcriptHistory
    },
    transcriptReady(state: ConversationState): boolean {
      return state.transcriptReady
    },
    searchWordToHashMap(state: ConversationState) {
      return state.searchWordToHashMap
    }
  },
  mutations: {
    SET_TRANSCRIPT(state: ConversationState, transcript: Transcript) {
      state.originalTranscript = transcript
      state.transcript = transcriptToUI(transcript)
      state.editedTranscript = transcriptToUI(transcript)
    },
    TOGGLE_SEARCH(state: ConversationState, status?: boolean) {
      if (status !== undefined) {
        state.isSearchOpen = status
      } else {
        state.isSearchOpen = !state.isSearchOpen
      }

      state.searchQuery = ''
      state.searchResults = []

      state.searchWordToHashMap = {}
      state.activeSearchResultIndex = 0
    },
    SET_SEARCH_QUERY(state: ConversationState, query: string) {
      state.searchQuery = query
      if (query) {
        // remove redundant whitespaces and trim
        const normalizedQuery: string = query.replace(/\s+/g, ' ').trim()
        const searchResults: Word[] = []

        const uniqueWords: string[] = uniq(normalizedQuery.split(' '))

        const searchHashMap = {}

        uniqueWords.forEach(word => {
          searchHashMap[word] = true
        })

        state.searchWordToHashMap = searchHashMap

        uniqueWords.forEach((word: string) => {
          if (state.transcript) {
            const resultsFound = state.transcript.words.filter(
              (w: Word) =>
                w.text.toLowerCase() ===
                excludeEndingPunctuation(word.toLowerCase())
            )
            searchResults.push(...resultsFound)
          }
        })

        state.searchResults = [
          ...searchResults.sort((r1: Word, r2: Word) => r1.start - r2.start)
        ]

        if (
          state.activeSearchResultIndex >= state.searchResults.length &&
          state.searchResults.length
        ) {
          state.activeSearchResultIndex = state.searchResults.length - 1
        }
      } else {
        state.searchResults = []
        state.searchWordToHashMap = {}
        state.activeSearchResultIndex = 0
      }
    },
    NEXT_SEARCH_RESULT(state: ConversationState) {
      if (state.activeSearchResultIndex === state.searchResults.length - 1) {
        state.activeSearchResultIndex = 0
      } else {
        state.activeSearchResultIndex += 1
      }
    },
    PREVIOUS_SEARCH_RESULT(state: ConversationState) {
      if (state.activeSearchResultIndex === 0) {
        state.activeSearchResultIndex = state.searchResults.length - 1
      } else {
        state.activeSearchResultIndex -= 1
      }
    },
    TOGGLE_SPEAKERS_LIST(state: ConversationState, partTimecode: number) {
      if (partTimecode || partTimecode === 0) {
        state.isSpeakersListVisible = true
        state.selectedSpeakerPartTimecode = partTimecode
      } else {
        state.isSpeakersListVisible = false
        state.selectedSpeakerPartTimecode = undefined
      }
    },
    ADD_SPEAKER_AND_SELECT(state: ConversationState, speaker: UISpeaker) {
      state.suggestedSpeakersList.push(speaker)
    },
    DELETE_SPEAKER(state: ConversationState, speakerId: number) {
      state.suggestedSpeakersList = state.suggestedSpeakersList.filter(
        currentSpeaker => currentSpeaker.id !== speakerId
      )
    },
    TOGGLE_SELECTION_POPUP(state: ConversationState, status?: boolean) {
      if (status !== undefined) {
        state.isSelectionPopupShown = status
      } else {
        state.isSelectionPopupShown = !state.isSelectionPopupShown
      }
    },
    SET_SELECTION_BOUNDARIES(
      state: ConversationState,
      boundaries: DOMRect | null
    ) {
      state.selectionBoundaries = boundaries
    },
    TOGGLE_AUDIO(state: ConversationState, status?: boolean) {
      if (status !== undefined) {
        state.isAudioPlaying = status
      } else {
        state.isAudioPlaying = !state.isAudioPlaying
      }
      state.continueAudioAfterAction = false
    },
    TOGGLE_AUDIO_CONTINUE_AFTER_ACTION(
      state: ConversationState,
      status?: boolean
    ) {
      if (status !== undefined) {
        state.continueAudioAfterAction = status
      } else {
        state.continueAudioAfterAction = !state.continueAudioAfterAction
      }
    },
    TOGGLE_IS_EDITABLE(state: ConversationState, status?: boolean) {
      state.lastWordDisplayed = false
      if (status !== undefined) {
        state.isEditable = status
      } else {
        state.isEditable = !state.isEditable
      }
    },
    UPDATE_TRANSCRIPT_SPEAKERS(state: ConversationState, speaker: UISpeaker) {
      if (!state.transcript) {
        return
      }

      state.transcript.parts.forEach((p: Word) => {
        if (p.start === state.selectedSpeakerPartTimecode) {
          p.speaker = speaker
        }
      })
    },
    UNTAG_SPEAKER(state: ConversationState, speaker: UISpeaker) {
      if (!state.transcript) {
        return
      }

      state.transcript.parts.forEach((p: Word) => {
        if (
          p.speaker.id === speaker.id &&
          p.start === state.selectedSpeakerPartTimecode
        ) {
          p.speaker = {
            ...p.speaker,
            id: undefined,
            name: `Unknown speaker`
          }
        }
      })
    },
    SAVE(state: ConversationState, status: boolean) {
      state.shouldSave = status
    },

    RESTORE_TRANSCRIPT(state: ConversationState, transcript: UITranscript) {
      state.transcript = null
      state.transcript = transcript
      state.wordsByTenSeconds = mapWordsByTenSeconds(state.transcript.words)
      state.suggestedSpeakersList = mapUITranscriptSpeakers(state.transcript)
    },

    UPDATE_TRANSCRIPT(state: ConversationState, transcript: UITranscript) {
      state.transcript = transcript
      state.wordsByTenSeconds = mapWordsByTenSeconds(state.transcript.words)
      state.shouldSave = false
    },

    INIT_TRANSCRIPT(state: ConversationState, transcript: Transcript) {
      state.originalTranscript = transcript
      state.transcript = transcriptToUI(transcript)
      state.suggestedSpeakersList = mapSpeakers(state.originalTranscript)
      state.wordsByTenSeconds = mapWordsByTenSeconds(state.transcript.words)
      // state.transcriptHistory.push({
      //     transcript: state.transcript,
      //     activePartIndex: 0,
      //     focusedWordId: '',
      //     cursorPosition: 0
      // })
    },

    SET_PLAYER_TIME(state: ConversationState, time: number) {
      state.playerTime = time * 1000

      function getSpelledDecade(): string | undefined {
        return Object.keys(state.wordsByTenSeconds!).find(key => {
          const _key = +key * 1000
          return state.playerTime <= _key && state.playerTime >= _key - 10000
        })
      }

      function getSpelledWords() {
        return state.spelledSlice.filter(w => {
          return (
            (w.start <= state.playerTime && w.end >= state.playerTime) ||
            w.end === state.playerTime
          )
        })
      }

      function updateSpelledWords(words: Word[]) {
        if (!state.spelledWords.length) {
          state.spelledWords = words
        }

        if (JSON.stringify(state.spelledWords) !== JSON.stringify(words)) {
          state.spelledWords = words
        }

        if (state.spelledWords.length) {
          state.spelledPartId = state.spelledWords[0].partId || ''
        }
      }

      if (state.wordsByTenSeconds) {
        const spelledDecade = getSpelledDecade()

        if (!spelledDecade) {
          return
        }

        if (state.spelledDecade !== +spelledDecade) {
          state.spelledDecade = +spelledDecade
          state.spelledSlice = state.wordsByTenSeconds[+spelledDecade]
        }

        if (!state.spelledSlice.length) {
          return
        }

        const spelledWords = getSpelledWords()

        if (!spelledWords.length) {
          return
        }

        updateSpelledWords(spelledWords)
      }
    },

    SET_PLAYER_STATUS(state: ConversationState, status: boolean) {
      state.isPlaying = status
    },

    LAST_WORD_DISPLAYED(state: ConversationState, time: number) {
      state.lastWordDisplayed = true
    },

    EDITOR_LOADING(state: ConversationState, status: boolean) {
      state.editorLoading = status
    },

    SET_BROWSER(state: ConversationState, browser: string) {
      state.browser = browser
    },

    UNDO(state: ConversationState, status: boolean) {
      state.shouldUndo = status

      setTimeout(() => {
        state.shouldUndo = false
      }, 200)
    },

    UPDATE_TRANSCRIPT_HISTORY(
      state: ConversationState,
      transcript: TranscriptHistory
    ) {
      state.transcriptHistory.push(transcript)
    },

    INSERT_PART(state: ConversationState, data: { part: Word; index: number }) {
      if (!state.transcript) {
        return
      }

      state.transcript.parts.splice(data.index, 0, data.part)
    },

    REMOVE_LAST_HISTORY_ITEM(state: ConversationState) {
      state.transcriptHistory.pop()
    },

    TRANSCRIPT_READY(state: ConversationState, status: boolean) {
      state.transcriptReady = status
    }
  },
  actions: {
    setTranscript({ commit }: { commit: Commit }, transcript: Transcript) {
      commit('SET_TRANSCRIPT', transcript)
    },
    toggleSearch({ commit }: { commit: Commit }, status?: boolean) {
      commit('TOGGLE_SEARCH', status)
    },
    setSearchQuery({ commit }: { commit: Commit }, query: string) {
      commit('SET_SEARCH_QUERY', query)
    },
    previousSearchResult({ commit }: { commit: Commit }) {
      commit('PREVIOUS_SEARCH_RESULT')
    },
    nextSearchResult({ commit }: { commit: Commit }) {
      commit('NEXT_SEARCH_RESULT')
    },
    updateTranscriptSpeakers(
      { commit }: { commit: Commit },
      newSpeaker: UISpeaker
    ) {
      commit('UPDATE_TRANSCRIPT_SPEAKERS', newSpeaker)
    },
    addSpeakerAndSelect({ commit }: { commit: Commit }, speaker: UISpeaker) {
      commit('ADD_SPEAKER_AND_SELECT', speaker)
    },
    deleteSpeaker({ commit }: { commit: Commit }, speakerId: number) {
      commit('DELETE_SPEAKER', speakerId)
    },
    toggleSpeakersList(
      { commit }: { commit: Commit },
      partTimecode: number | null
    ) {
      commit('TOGGLE_SPEAKERS_LIST', partTimecode)
    },
    toggleSelectionPopup({ commit }: { commit: Commit }, status?: boolean) {
      commit('TOGGLE_SELECTION_POPUP', status)
    },
    setSelectionBoundaries(
      { commit }: { commit: Commit },
      boundaries?: DOMRect | null
    ) {
      commit('SET_SELECTION_BOUNDARIES', boundaries)
    },
    cancelSelection({ commit }: { commit: Commit }) {
      commit('TOGGLE_SELECTION_POPUP', false)
      commit('SET_SELECTION_BOUNDARIES', null)
    },
    toggleAudio({ commit }: { commit: Commit }, status?: boolean) {
      commit('TOGGLE_AUDIO', status)
    },
    stopAudioToContinue({ commit }: { commit: Commit }) {
      commit('TOGGLE_AUDIO', false)
      commit('TOGGLE_AUDIO_CONTINUE_AFTER_ACTION', true)
    },
    toggleIsEditable({ commit }: { commit: Commit }, status?: boolean) {
      commit('TOGGLE_IS_EDITABLE', status)
    },
    untagSpeaker({ commit }: { commit: Commit }, speaker: UISpeaker) {
      commit('UNTAG_SPEAKER', speaker)
    },
    save({ commit }: { commit: Commit }, status: boolean) {
      commit('SAVE', status)
    },
    undo({ commit }: { commit: Commit }, status: boolean) {
      commit('UNDO', status)
    },
    updateTranscript({ commit }: { commit: Commit }, transcript: UITranscript) {
      commit('UPDATE_TRANSCRIPT', transcript)
    },
    initTranscript({ commit }: { commit: Commit }, transcript: Transcript) {
      commit('INIT_TRANSCRIPT', transcript)
    },
    setPlayerTime({ commit }: { commit: Commit }, time: number) {
      commit('SET_PLAYER_TIME', time)
    },
    setPlayerStatus({ commit }: { commit: Commit }, status: boolean) {
      commit('SET_PLAYER_STATUS', status)
    },
    lastWordDisplayed({ commit }: { commit: Commit }, time: number) {
      commit('LAST_WORD_DISPLAYED', time)
    },
    editorLoading({ commit }: { commit: Commit }, status: boolean) {
      commit('EDITOR_LOADING', status)
    },
    setBrowser({ commit }: { commit: Commit }, browser: string) {
      commit('SET_BROWSER', browser)
    },
    restoreTranscript(
      { commit }: { commit: Commit },
      transcript: UITranscript
    ) {
      commit('RESTORE_TRANSCRIPT', transcript)
    },
    updateTranscriptHistory(
      { commit }: { commit: Commit },
      transcript: UITranscript
    ) {
      commit('UPDATE_TRANSCRIPT_HISTORY', transcript)
    },
    removeLastHistoryItem({ commit }: { commit: Commit }) {
      commit('REMOVE_LAST_HISTORY_ITEM')
    },
    transcriptReady({ commit }: { commit: Commit }, status: boolean) {
      commit('TRANSCRIPT_READY', status)
    },
    insertPart(
      { commit }: { commit: Commit },
      data: { part: Word; index: number }
    ) {
      commit('INSERT_PART', data)
    }
  }
}
